import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './shared/Card';
import { ProgressBar } from './shared/ProgressBar';
import { ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
type ProfileSetupProps = {
  onComplete: () => void;
};
export const ProfileSetup: React.FC<ProfileSetupProps> = ({
  onComplete
}) => {
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        return <Card title="Birth Information (IBF)" data-id="element-761">
            <div className="space-y-4" data-id="element-762">
              <div className="space-y-2" data-id="element-763">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-764">
                  Date of Birth
                </label>
                <input type="date" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-765" />
              </div>
              <div className="space-y-2" data-id="element-766">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-767">
                  Time of Birth
                </label>
                <input type="time" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-768" />
              </div>
              <div className="space-y-2" data-id="element-769">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-770">
                  Place of Birth
                </label>
                <input type="text" placeholder="City, Country" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-771" />
              </div>
            </div>
          </Card>;
      case 2:
        return <Card title="Educational & Professional Information (EPI)" data-id="element-772">
            <div className="space-y-4" data-id="element-773">
              <div className="space-y-2" data-id="element-774">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-775">
                  Highest Education
                </label>
                <select className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-776">
                  <option data-id="element-777">Select education level</option>
                  <option data-id="element-778">High School</option>
                  <option data-id="element-779">Bachelor's Degree</option>
                  <option data-id="element-780">Master's Degree</option>
                  <option data-id="element-781">PhD</option>
                </select>
              </div>
              <div className="space-y-2" data-id="element-782">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-783">
                  Field of Study/Major
                </label>
                <input type="text" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-784" />
              </div>
              <div className="space-y-2" data-id="element-785">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-786">
                  Years of Work Experience
                </label>
                <input type="number" min="0" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-787" />
              </div>
            </div>
          </Card>;
      case 3:
        return <Card title="Career Interests & Values (CIV)" data-id="element-788">
            <div className="space-y-4" data-id="element-789">
              <div className="space-y-2" data-id="element-790">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-791">
                  Top 3 Career Interests
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-792">
                  {['Technology', 'Healthcare', 'Finance', 'Education', 'Arts', 'Science'].map(interest => <div key={interest} className="flex items-center" data-id="element-793">
                      <input type="checkbox" id={interest} className="w-4 h-4 accent-[#7D56F4]" data-id="element-794" />
                      <label htmlFor={interest} className="ml-2 text-sm" data-id="element-795">
                        {interest}
                      </label>
                    </div>)}
                </div>
              </div>
              <div className="space-y-2" data-id="element-796">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-797">
                  Work Style Preference
                </label>
                <select className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-798">
                  <option data-id="element-799">Select preference</option>
                  <option data-id="element-800">Remote work</option>
                  <option data-id="element-801">Office-based</option>
                  <option data-id="element-802">Hybrid</option>
                  <option data-id="element-803">Flexible</option>
                </select>
              </div>
              <div className="space-y-2" data-id="element-804">
                <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-805">
                  Most Important Value
                </label>
                <select className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-806">
                  <option data-id="element-807">Select value</option>
                  <option data-id="element-808">Work-life balance</option>
                  <option data-id="element-809">High income</option>
                  <option data-id="element-810">Making an impact</option>
                  <option data-id="element-811">Learning & growth</option>
                  <option data-id="element-812">Job security</option>
                </select>
              </div>
            </div>
          </Card>;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="max-w-md mx-auto" data-id="element-813">
      <div className="mb-8 text-center" data-id="element-814">
        <h1 className="text-2xl font-bold text-white mb-2" data-id="element-815">
          Create Your Cosmic Profile
        </h1>
        <p className="text-[#B8C0E0]" data-id="element-816">
          Step {step} of {totalSteps}
        </p>
        <ProgressBar progress={step / totalSteps * 100} data-id="element-817" />
      </div>
      <motion.div key={step} initial={{
      x: 20,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: -20,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-818">
        {renderStep()}
      </motion.div>
      <div className="mt-8 flex justify-end" data-id="element-819">
        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={nextStep} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-2 px-6 rounded-full flex items-center" data-id="element-820">
          {step === totalSteps ? <>
              <span data-id="element-821">Complete</span>
              <CheckCircleIcon size={18} className="ml-2" data-id="element-822" />
            </> : <>
              <span data-id="element-823">Continue</span>
              <ArrowRightIcon size={18} className="ml-2" data-id="element-824" />
            </>}
        </motion.button>
      </div>
    </motion.div>;
};